import React, { useState } from 'react';
import { supabase } from '../../supabaseClient';
import styles from './Auth.module.css';
import Button from '../ui/Button/Button';
import axios from 'axios'; // Import axios for backend API calls

interface AuthProps {
  onAuthChange: () => void; // Callback to notify App.tsx of auth state change
}

const Auth: React.FC<AuthProps> = ({ onAuthChange }) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const API_BASE_URL = import.meta.env.VITE_APP_BACKEND_API_URL || 'http://localhost:5000';
  const FRONTEND_URL = import.meta.env.VITE_APP_FRONTEND_URL || 'http://localhost:5173'; // Default to Vite's default port

  const validateForm = () => {
    if (!email || !password) {
      setError('Email and password are required.');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Invalid email format.');
      return false;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return false;
    }
    setError(null);
    return true;
  };

  const handleAuth = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      let authResponse;
      if (isSignUp) {
        authResponse = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${FRONTEND_URL}/dashboard`, // Redirect to dashboard after email confirmation
          },
        });
        if (authResponse.error) throw authResponse.error;
        setMessage('Sign up successful! Please check your email to confirm your account.');

        // After successful signup, associate existing invoices with the new user
        if (authResponse.data.user) {
          try {
            const associateResponse = await axios.post(`${API_BASE_URL}/api/associate-invoices`, {
              userId: authResponse.data.user.id,
            });
            console.log('Associate invoices response:', associateResponse.data.message);
          } catch (associateError: any) {
            console.error('Error associating invoices:', associateError.response?.data || associateError.message);
            // Do not block login flow, but log the error
          }
        }
      } else {
        authResponse = await supabase.auth.signInWithPassword({ email, password });
        if (authResponse.error) throw authResponse.error;
        setMessage('Signed in successfully!');
      }

      // If authentication is successful, trigger the onAuthChange callback
      if (authResponse.data.user) {
        onAuthChange();
      }
    } catch (err: any) {
      console.error('Auth error:', err);
      setError(err.message || 'An unexpected error occurred during authentication.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.authContainer}>
      <h2 className={styles.authTitle}>{isSignUp ? 'Sign Up' : 'Sign In'}</h2>
      <form onSubmit={handleAuth} className={styles.authForm}>
        <div className={styles.formGroup}>
          <label htmlFor="email" className={styles.label}>Email:</label>
          <input
            type="email"
            id="email"
            className={styles.input}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="password" className={styles.label}>Password:</label>
          <input
            type="password"
            id="password"
            className={styles.input}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
          />
        </div>
        {error && <p className={styles.errorMessage}>{error}</p>}
        {message && <p className={styles.successMessage}>{message}</p>}
        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? 'Loading...' : (isSignUp ? 'Sign Up' : 'Sign In')}
        </Button>
      </form>
      <p className={styles.toggleAuth}>
        {isSignUp ? 'Already have an account?' : 'Need an account?'}
        <button
          type="button"
          className={styles.toggleButton}
          onClick={() => setIsSignUp(!isSignUp)}
          disabled={loading}
        >
          {isSignUp ? 'Sign In' : 'Sign Up'}
        </button>
      </p>
    </div>
  );
};

export default Auth;
